/**
 * Pricing — PAYG prepaid energy (ADR 0002).
 *
 * No subscription. New users get a free starter grant; top-up packs add energy
 * (1 energy ≈ 1 text card; audio/video cost more by length). Copy avoids "unlimited" (per-unit-cost axis) and
 * never frames it as "AI cost" — energy is a feature currency.
 *
 * v10-A4: copy routes through landing.* i18n keys (locale from ?lang / ref
 * hint / navigator.languages — see LandingI18n.tsx).
 */

import { Check } from 'lucide-react'
import { TOPUP_PACKS } from '@shared/pricing'
import { useScrollReveal } from '../../hooks/useScrollReveal'
import { useLandingT, usePublicHref } from './landing-locale'

export function Pricing() {
  const revealRef = useScrollReveal()
  const t = useLandingT()
  const href = usePublicHref()

  return (
    <section id="pricing" className="py-16 md:py-24 bg-surface">
      <div ref={revealRef} className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
        <h2 className="reveal font-headline font-bold text-3xl sm:text-4xl text-on-surface text-center mb-4">
          {t('landing.pricing.title')}
        </h2>
        <p className="reveal text-on-surface-variant text-lg text-center mb-12">
          {t('landing.pricing.subtitle')}
        </p>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 max-w-3xl mx-auto">
          {/* Starter — free signup grant */}
          <div className="reveal reveal-stagger-1 bg-surface-container-low rounded-2xl p-8 border border-outline-variant">
            <h3 className="font-headline font-bold text-xl text-on-surface mb-1">{t('landing.pricing.starter')}</h3>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="font-headline font-extrabold text-4xl text-on-surface">{t('landing.pricing.starterPrice')}</span>
              <span className="text-muted-soft text-sm">{t('landing.pricing.starterToBegin')}</span>
            </div>
            <ul className="space-y-3 mb-8">
              {[
                t('landing.pricing.starterGrant'),
                t('landing.pricing.starterAiImage'),
                t('landing.pricing.starterSwitch'),
                t('landing.pricing.starterSrs'),
                t('landing.pricing.starterDevices'),
                t('landing.pricing.starterSync'),
              ].map((item) => (
                <li key={item} className="flex items-start gap-2.5 text-sm text-on-surface-variant">
                  <Check className="w-4 h-4 text-success mt-0.5 shrink-0" strokeWidth={2} />
                  {item}
                </li>
              ))}
            </ul>
            <a
              href={href('/login')}
              className="block text-center px-6 py-3 rounded-full border-2 border-outline-variant text-on-surface font-bold text-sm hover:bg-surface-container-low transition-colors"
            >
              {t('landing.pricing.starterCta')}
            </a>
          </div>

          {/* Top-up packs */}
          <div className="reveal reveal-stagger-2 hover-lift bg-surface-container-low rounded-2xl p-8 border-2 border-primary relative shadow-elevated">
            <div className="absolute -top-3 left-1/2 -translate-x-1/2 px-4 py-1 bg-primary text-on-primary text-xs font-bold rounded-full">
              Best value
            </div>
            <h3 className="font-headline font-bold text-xl text-on-surface mb-1">{t('landing.pricing.topup')}</h3>
            <div className="flex items-baseline gap-1 mb-6">
              <span className="font-headline font-extrabold text-4xl text-on-surface">500</span>
              <span className="text-muted-soft text-sm">{t('landing.pricing.topupCards')}</span>
            </div>
            <ul className="space-y-3 mb-8">
              <li className="flex items-start gap-2.5 text-sm text-on-surface-variant">
                <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" strokeWidth={2} />
                {t('landing.pricing.topupRate')}
              </li>
              {TOPUP_PACKS.map((pack) => {
                const bonus = pack.bonusPercent > 0 ? ` (+${pack.bonusPercent}%)` : ''
                const label = `$${pack.amount} — ${pack.energy.toLocaleString()} energy${bonus}`
                return (
                  <li key={pack.amount} className="flex items-start gap-2.5 text-sm text-on-surface-variant">
                    <Check className="w-4 h-4 text-primary mt-0.5 shrink-0" strokeWidth={2} />
                    {label}
                  </li>
                )
              })}
            </ul>
            <a
              href={href('/login')}
              className="block text-center px-6 py-3 rounded-full bg-primary text-on-primary font-bold text-sm hover:bg-primary-active transition-colors shadow-card"
            >
              {t('landing.pricing.starterCta')}
            </a>
          </div>
        </div>
      </div>
    </section>
  )
}
